import { useTutorialContext } from '@resistapp/client/contexts/tutorial-context';
import { useTutorial } from '@resistapp/client/hooks/use-tutorial';
import { OverviewChartConfiguration } from '@resistapp/client/utils/overview-chart-configurations';
import { MetricMode } from '@resistapp/common/types';
import { TutorialContainer } from '../tooltips/tutorial-container';
import { InfoIconProps } from './info-icons-general';
import { ReductionInfoIcon } from './reduction-info-icon';
import { ResistanceIndexInfoIcon } from './resistance-index-info-icon';
import { RiskScoreInfoIcon } from './risk-score-info-icon';

interface Props {
  metricMode: MetricMode;
  infoIconProps?: InfoIconProps;
  activeOverviewConfiguration?: OverviewChartConfiguration;
  isMobile?: boolean;
  useTutorial?: boolean;
}

export function LegendInfoIcon({
  metricMode,
  infoIconProps = {},
  activeOverviewConfiguration,
  isMobile,
  useTutorial: enableTutorial,
}: Props) {
  // Always call hooks unconditionally at the top level
  const { setTutorialChanged } = useTutorialContext();
  const { currentTutorialStep, tutorial, nextStep, prevStep, stopTutorial } = useTutorial('overview');

  // Determine if we're in the tutorial step 4
  const isTutorialStep = enableTutorial && currentTutorialStep === 4;

  // Render the appropriate info icon based on metric mode
  const renderInfoIcon = () => {
    switch (metricMode) {
      case MetricMode.RISK:
        return <RiskScoreInfoIcon {...infoIconProps} />;
      case MetricMode.ARGI:
        return <ResistanceIndexInfoIcon {...infoIconProps} />;
      case MetricMode.REDUCTION:
        return <ReductionInfoIcon {...infoIconProps} activeOverviewConfiguration={activeOverviewConfiguration} />;
      default:
        return null;
    }
  };

  // First just render the info icon directly
  const infoIcon = renderInfoIcon();

  // If tutorial is active and we're on step 4, add a tutorial container that targets the icon
  if (isTutorialStep) {
    return (
      <>
        {/* Render the actual info icon outside the TutorialContainer */}
        {infoIcon}

        {/* Add tutorial overlay that targets the icon but doesn't change its positioning */}
        <div
          style={{
            position: 'absolute',
            right: infoIconProps.style?.right,
            bottom: infoIconProps.style?.bottom,
            zIndex: infoIconProps.style?.zIndex || 20,
            marginRight: '15px',
            marginBottom: '5px',
          }}
        >
          <TutorialContainer
            currentTutorialStep={currentTutorialStep}
            thisTutorialStep={4}
            tutorial={tutorial}
            setTutorialChanged={setTutorialChanged}
            nextStep={nextStep}
            prevStep={prevStep}
            stopTutorial={stopTutorial}
            isMobile={!!isMobile}
            position="bottom"
            style={{ marginTop: '-60px' }}
          >
            {/* Empty div as trigger in the exact same position as the info icon */}
            <div style={{ width: '20px', height: '20px', position: 'relative', top: '-4px', right: '-40px' }} />
            <div>
              <p>Click on the question mark icons to learn about metrics and their meanings.</p>
              <br />
              <p>These info icons can be found throughout the application.</p>
            </div>
          </TutorialContainer>
        </div>
      </>
    );
  }

  // Otherwise just render the info icon
  return infoIcon;
}
